import { LmsAssignmentResponse } from '../lms/LmsAssignmentResponse';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Eye } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { getKidSub } from '@/auth/authStore';
import {
  clearActiveStudyAssignment,
  clearStudyAssignmentIfDone,
  setActiveStudyAssignment,
  STUDY_ASSIGNMENT_NAV_STATE_KEY,
} from '@/lib/studyAssignmentContext';
import { AcademyQuestionVisual } from '../academy/AcademyQuestionVisual';
import { AcademyRichText } from '../academy/AcademyRichText';
import { studyAccessMessage } from './studyAccessMessages';
import {
  completeMyStudyAssignment,
  formatAssignmentDue,
  getMyStudyAssignment,
  practiceYearLevel,
  recordAssignmentToolOpened,
  startMyStudyAssignment,
  studyAssignmentQueryKey,
  STUDY_ASSIGNMENTS_QUERY_KEY,
  submitAssignmentPracticeAttempt,
  type MyStudyAssignmentDetail,
  type StudyAssignmentQuestion,
} from './studyAssignmentsApi';
import { StudyShell } from './StudyShell';
import { STUDY_TOOLS } from './studyTools';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];
export const STUDY_ASSIGNMENT_NOTICE =
  'Your teacher can see your answers for this task. Your own Study stays private.';

function messageFor(error: unknown, fallback: string): string {
  return studyAccessMessage(error) ?? fallback;
}

function StartPanel({ assignment }: { assignment: MyStudyAssignmentDetail }) {
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const start = useMutation({
    mutationFn: () => startMyStudyAssignment(assignment.id),
    onSuccess: async (detail) => {
      queryClient.setQueryData(studyAssignmentQueryKey(assignment.id), detail);
      await queryClient.invalidateQueries({ queryKey: STUDY_ASSIGNMENTS_QUERY_KEY });
    },
  });
  return (
    <section
      aria-labelledby="assignment-start-title"
      className="rounded-3xl border border-brand-sky/30 bg-wash-sky p-5 sm:p-7"
    >
      <h2 id="assignment-start-title" className="flex items-center gap-2 text-xl font-extrabold text-ink">
        <Eye className="h-6 w-6 text-brand-sky" aria-hidden="true" />
        Before you start
      </h2>
      <p className="mt-3 font-semibold leading-relaxed text-ink">{STUDY_ASSIGNMENT_NOTICE}</p>
      <label className="mt-4 flex items-center gap-3 font-bold text-ink">
        <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
        I understand
      </label>
      <button
        type="button"
        className="btn-pill-primary mt-5 disabled:cursor-not-allowed disabled:opacity-45"
        disabled={!agreed || start.isPending || assignment.assignment_status !== 'published'}
        onClick={() => start.mutate()}
      >
        {start.isPending ? 'Starting…' : 'Start task'}
      </button>
      {start.isError && (
        <p className="mt-3 font-bold text-brand-coral" role="alert">
          {messageFor(start.error, 'Could not start this task. Try again.')}
        </p>
      )}
    </section>
  );
}

function PracticeRunner({ assignment }: { assignment: MyStudyAssignmentDetail }) {
  const queryClient = useQueryClient();
  const questions = assignment.practice?.questions ?? [];
  const firstOpen = questions.findIndex((question) => !question.attempted);
  const [index, setIndex] = useState(firstOpen === -1 ? 0 : firstOpen);
  const [selected, setSelected] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ is_correct: boolean; correct_answer: string | null } | null>(
    null,
  );
  const current: StudyAssignmentQuestion | undefined = questions[index];
  const attempt = useMutation({
    mutationFn: (value: string) => {
      const yearLevel = practiceYearLevel(current?.year_level);
      if (!current || !yearLevel) throw new Error('This question has no Year level.');
      return submitAssignmentPracticeAttempt({
        assignment_id: assignment.id,
        subject_key: assignment.subject_key,
        year_level: yearLevel,
        question_id: current.id,
        submitted: value,
      });
    },
    onSuccess: async (result) => {
      setFeedback(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: studyAssignmentQueryKey(assignment.id) }),
        queryClient.invalidateQueries({ queryKey: STUDY_ASSIGNMENTS_QUERY_KEY }),
      ]);
    },
  });

  if (questions.length === 0) {
    return (
      <p className="rounded-3xl bg-wash-sunshine p-5 font-semibold text-ink">
        The questions for this task are not available right now. Ask your teacher.
      </p>
    );
  }
  if (!current) return null;
  const next = () => {
    setSelected(null);
    setFeedback(null);
    setIndex((value) => Math.min(value + 1, questions.length - 1));
  };

  return (
    <section className="rounded-3xl border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-8">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
        Question {index + 1} of {questions.length}
      </p>
      {current.stimulus && (
        <div className="mt-4 rounded-2xl bg-wash-sky p-4">
          <p className="whitespace-pre-line font-semibold leading-relaxed text-ink">{current.stimulus.text}</p>
        </div>
      )}
      <h3 className="mt-4 text-2xl font-bold leading-relaxed text-ink">
        <AcademyRichText text={current.stem_text} />
      </h3>
      <AcademyQuestionVisual spec={current.render_spec} figureKeys={current.figure_keys} />
      <div className="mt-6 grid gap-3">
        {(current.options ?? []).map((option, optionIndex) => {
          const value = LETTERS[optionIndex] ?? String(optionIndex + 1);
          return (
            <button
              key={`${value}-${option}`}
              type="button"
              disabled={feedback !== null}
              onClick={() => setSelected(value)}
              className={`flex min-h-14 items-center gap-4 rounded-2xl border-2 px-4 py-3 text-left ${
                feedback?.correct_answer === value
                  ? 'border-brand-mint bg-wash-mint'
                  : selected === value
                    ? 'border-brand-sky bg-wash-sky'
                    : 'border-hairline bg-canvas-pure'
              }`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface font-black text-ink">
                {value}
              </span>
              <span className="font-bold text-ink">
                <AcademyRichText text={option} />
              </span>
            </button>
          );
        })}
      </div>
      {!feedback ? (
        <button
          type="button"
          className="btn-pill-primary mt-6 disabled:cursor-not-allowed disabled:opacity-45"
          disabled={!selected || attempt.isPending}
          onClick={() => selected && attempt.mutate(selected)}
        >
          {attempt.isPending ? 'Checking…' : 'Check answer'}
        </button>
      ) : (
        <div className={`mt-6 rounded-2xl p-4 ${feedback.is_correct ? 'bg-wash-mint' : 'bg-wash-coral'}`} aria-live="polite">
          <p className="font-extrabold text-ink">{feedback.is_correct ? 'Correct.' : 'Not quite.'}</p>
          {index + 1 < questions.length && (
            <button type="button" className="btn-pill-secondary mt-3" onClick={next}>
              Next question
            </button>
          )}
        </div>
      )}
      {attempt.isError && (
        <p className="mt-3 font-bold text-brand-coral" role="alert">
          {messageFor(attempt.error, 'Could not save this answer. Try again.')}
        </p>
      )}
    </section>
  );
}

function MaterialRunner({ assignment }: { assignment: MyStudyAssignmentDetail }) {
  const queryClient = useQueryClient();
  const [response, setResponse] = useState(assignment.response_text ?? '');
  const complete = useMutation({
    mutationFn: () => completeMyStudyAssignment(assignment.id, response.trim() || undefined),
    onSuccess: async (detail) => {
      queryClient.setQueryData(studyAssignmentQueryKey(assignment.id), detail);
      await queryClient.invalidateQueries({ queryKey: STUDY_ASSIGNMENTS_QUERY_KEY });
    },
  });
  const done = assignment.status === 'completed';
  return (
    <section className="rounded-3xl border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-8">
      <ul className="grid gap-3">
        {(assignment.material?.items ?? []).map((item) => {
          const tool = item.tool_key ? STUDY_TOOLS.find((entry) => entry.id === item.tool_key) : null;
          return (
            <li key={item.id} className="rounded-2xl border border-hairline p-4">
              {item.kind === 'link' && item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-extrabold text-brand-sky underline"
                >
                  {item.title}
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              ) : tool ? (
                <Link to={tool.path} className="font-extrabold text-brand-sky underline">
                  {item.title}
                </Link>
              ) : (
                <span className="font-extrabold text-ink">{item.title}</span>
              )}
            </li>
          );
        })}
      </ul>
      <label className="mt-5 block font-bold text-ink" htmlFor="assignment-response">
        Note for your teacher (optional)
      </label>
      <textarea
        id="assignment-response"
        className="mt-2 min-h-24 w-full rounded-2xl border border-hairline p-3 text-ink"
        maxLength={2000}
        value={response}
        disabled={done}
        onChange={(event) => setResponse(event.target.value)}
      />
      {done ? (
        <p className="mt-4 flex items-center gap-2 font-extrabold text-ink">
          <CheckCircle2 className="h-5 w-5 text-brand-mint" aria-hidden="true" />
          Marked as done
        </p>
      ) : (
        <button
          type="button"
          className="btn-pill-primary mt-4"
          disabled={complete.isPending || assignment.assignment_status !== 'published'}
          onClick={() => complete.mutate()}
        >
          {complete.isPending ? 'Saving…' : 'Mark as done'}
        </button>
      )}
      {complete.isError && (
        <p className="mt-3 font-bold text-brand-coral" role="alert">
          {messageFor(complete.error, 'Could not save. Try again.')}
        </p>
      )}
    </section>
  );
}

function ToolRunner({ assignment }: { assignment: MyStudyAssignmentDetail }) {
  const location = useLocation();
  const navigate = useNavigate();
  const tool = assignment.tool_key ? STUDY_TOOLS.find((entry) => entry.id === assignment.tool_key) : null;
  const open = useMutation({
    mutationFn: async () => {
      const kidId = getKidSub();
      if (!tool || !assignment.tool_key || !kidId) throw new Error('Tool unavailable');
      setActiveStudyAssignment({
        assignment_id: assignment.id,
        subject_key: assignment.subject_key,
        tool_key: assignment.tool_key,
        kid_id: kidId,
        tool_path: tool.path,
      });
      await recordAssignmentToolOpened({
        assignment_id: assignment.id,
        subject_key: assignment.subject_key,
        tool_key: assignment.tool_key,
      });
      return tool.path;
    },
    // Only this navigation arms the context; a later visit from the tools hub is never tagged.
    onSuccess: (path) =>
      navigate(`${path}${location.search}`, {
        state: { [STUDY_ASSIGNMENT_NAV_STATE_KEY]: assignment.id },
      }),
    onError: () => clearActiveStudyAssignment(),
  });
  if (!tool) {
    return (
      <p className="rounded-3xl bg-wash-sunshine p-5 font-semibold text-ink">
        This tool is not available in this app version. Ask your teacher.
      </p>
    );
  }
  return (
    <section className="rounded-3xl border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-8">
      <p className="font-semibold text-ink">
        Open <strong>{tool.name}</strong>. Answers you check in this tool count for this task. The
        task is marked done when your answers are recorded.
      </p>
      <button
        type="button"
        className="btn-pill-primary mt-5"
        disabled={open.isPending || assignment.assignment_status !== 'published'}
        onClick={() => open.mutate()}
      >
        {open.isPending ? 'Opening…' : `Open ${tool.name}`}
      </button>
      {open.isError && (
        <p className="mt-3 font-bold text-brand-coral" role="alert">
          {messageFor(open.error, 'Could not open the tool for this task. Try again.')}
        </p>
      )}
    </section>
  );
}

/** One Institution assignment, student side (SIR-TEA-01). */
export function StudyAssignmentPage() {
  const { assignmentId = '' } = useParams<{ assignmentId: string }>();
  const assignment = useQuery({
    queryKey: studyAssignmentQueryKey(assignmentId),
    queryFn: () => getMyStudyAssignment(assignmentId),
    enabled: assignmentId.length > 0,
    retry: false,
  });
  const data = assignment.data;
  useEffect(() => {
    if (data) clearStudyAssignmentIfDone(data);
  }, [data]);

  if (data?.lms_enabled) return <StudyShell><Link className="btn-pill-ghost inline-flex" to="/learn/study">← Back to Study</Link><LmsAssignmentResponse assignmentId={assignmentId}/></StudyShell>;

  return (
    <StudyShell>
      <Link to="/learn/study" className="text-sm font-extrabold text-ink-soft hover:text-ink">
        ← Back to Study
      </Link>
      {assignment.isLoading && <p className="lead-text mt-6">Opening your task…</p>}
      {assignment.isError && (
        <p className="mt-6 rounded-3xl bg-wash-coral p-5 font-bold text-ink" role="alert">
          {messageFor(assignment.error, 'This task could not load. Refresh and try again.')}
        </p>
      )}
      {data && (
        <div className="mt-6 grid gap-6">
          <header>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              From your teacher
            </p>
            <h1 className="mt-2 text-3xl font-extrabold text-ink">{data.title}</h1>
            <p className="mt-2 text-sm font-semibold text-slate2">
              {data.due_at ? `Due ${formatAssignmentDue(data.due_at)}` : 'No due date'}
              {data.overdue ? ' · Overdue' : ''}
              {data.assignment_status === 'closed' ? ' · Closed by your teacher' : ''}
              {data.status === 'completed' ? ' · Done' : ''}
            </p>
            {data.instructions && (
              <p className="mt-4 whitespace-pre-line font-medium leading-relaxed text-ink-soft">
                {data.instructions}
              </p>
            )}
          </header>
          {data.requires_visibility_ack ? (
            <StartPanel assignment={data} />
          ) : (
            <>
              <p className="rounded-2xl bg-wash-sky px-4 py-3 text-sm font-bold text-ink">
                {STUDY_ASSIGNMENT_NOTICE}
              </p>
              {data.kind === 'practice_set' && <PracticeRunner assignment={data} />}
              {data.kind === 'material' && <MaterialRunner assignment={data} />}
              {data.kind === 'tool' && <ToolRunner assignment={data} />}
            </>
          )}
        </div>
      )}
    </StudyShell>
  );
}
